import React, { memo, useEffect, useState } from 'react';
import { Modal } from '@/components/modal';
import PriceInput from '@/components/inputControls/PriceInput';
import { Button } from '@/components/button/Buttons';
import {
  BillingCycle,
  SubscriptionPayload,
} from '@/types/subscription-management.types';
import { Editor } from '@tinymce/tinymce-react';
import { tinymceInit, TINYMCE_SCRIPT_SRC } from '@/constant';
import { SubscriptionStatus } from '@/constant';
import { SubscriptionType } from '@/constant';
import { useLanguage, I18N_LANGUAGES } from '@/i18n';
import SubscriptionPreviewModal from './SubscriptionPreviewModal';
import { SubscriptionsService } from '@/services/subscriptions/subscriptionsService';
import { useSnackbar } from 'notistack';
import { useIntl } from 'react-intl';
import { DefaultTooltip } from '@/components/tooltip/Tooltip';

interface AddSubscriptionModalProps {
  isOpen: boolean;
  isCreating: boolean;
  onClose: () => void;
  onCreate: (payload: SubscriptionPayload) => void;
}

const initialData: SubscriptionPayload = {
  name: '',
  name_sub_title: '',
  description: '',
  price: undefined,
  billing_cycle: BillingCycle.MONTHLY,
  is_active: true,
  status: SubscriptionStatus.ACTIVE,
  apple_product_id: '',
};

const AddSubscriptionModal: React.FC<AddSubscriptionModalProps> = ({
  isOpen,
  isCreating,
  onClose,
  onCreate,
}) => {
  const [formData, setFormData] = useState<SubscriptionPayload>(initialData);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const { currentLanguage, changeLanguage } = useLanguage();
  const intl = useIntl();
  const [openPreview, setOpenPreview] = useState(false);
  // Store translations as a parsed object (not a string) so we can check
  // whether they already exist before deciding to call the translate API.
  const [translations, setTranslations] = useState<
    Record<string, Partial<{ name: string; description: string; name_sub_title: string }>> | undefined
  >(undefined);
  const [isTranslating, setIsTranslating] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  const stripTags = (html: string) =>
    html
      .replace(/<[^>]*>/g, '')
      .replace(/&nbsp;/g, ' ')
      .trim();
  const isRichTextEmpty = (html?: string) => {
    if (!html) return true;
    const trimmed = html.trim();
    if (!trimmed || trimmed === '<p><br></p>') return true;
    return stripTags(trimmed).length === 0;
  };

  const handleOpenPreview = async () => {
    if (isRichTextEmpty(formData.name)) return;
    // If translations already exist (generated earlier or loaded from API),
    // open the preview directly without calling the translate API again.
    if (translations && Object.keys(translations).length > 0) {
      setOpenPreview(true);
      return;
    }
    try {
      setIsTranslating(true);
      const result = await SubscriptionsService.translateSubscriptionContent({
        name: formData.name,
        name_sub_title: formData.name_sub_title,
        description: formData.description,
        source_language: currentLanguage.code,
      });
      setTranslations(result);
      setOpenPreview(true);
    } catch (error: unknown) {
      const e = error as {
        response?: { data?: { message?: string } };
        message?: string;
      };
      const msg =
        e?.response?.data?.message ||
        e?.message ||
        'Failed to prepare translations';
      enqueueSnackbar(msg, { variant: 'error' });
      // Still open preview with whatever we have
      setOpenPreview(true);
    } finally {
      setIsTranslating(false);
    }
  };

  useEffect(() => {
    if (!isOpen) {
      setFormData(initialData);
      setFormErrors({});
      setTranslations(undefined);
    }
  }, [isOpen]);

  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { id, value } = e.target;
    if (id === 'billing_cycle') {
      const numeric = Number(value);
      setFormData((prev) => ({
        ...prev,
        billing_cycle: Number.isNaN(numeric) ? prev.billing_cycle : numeric,
      }));
      return;
    }
    if (id === 'subscription_type') {
      const numeric = Number(value);
      setFormData((prev) => ({
        ...prev,
        subscription_type: Number.isNaN(numeric)
          ? prev.subscription_type
          : numeric,
      }));
      if (formErrors.subscription_type)
        setFormErrors((prev) => ({ ...prev, subscription_type: '' }));
      return;
    }
    if (id === 'status') {
      const statusVal = Number(value) as SubscriptionStatus;
      setFormData((prev) => ({
        ...prev,
        status: statusVal,
        is_active: statusVal === SubscriptionStatus.ACTIVE,
      }));
      return;
    }
    setFormData((prev) => ({ ...prev, [id]: value }));
    if (id === 'name' && formErrors.name)
      setFormErrors((prev) => ({ ...prev, name: '' }));
  };

  const validate = (): boolean => {
    const errors: Record<string, string> = {};
    if (isRichTextEmpty(formData.name)) {
      errors.name = 'Name is required';
    }
    if (
      formData.subscription_type === undefined ||
      formData.subscription_type === null ||
      formData.subscription_type === ('' as unknown as number)
    ) {
      errors.subscription_type = 'Subscription type is required';
    }
    if (
      formData.price !== undefined &&
      (isNaN(Number(formData.price)) || Number(formData.price) < 0)
    ) {
      errors.price = 'Price must be a non-negative number';
    }
    if (isRichTextEmpty(formData.description)) {
      // description is optional, but if provided as empty placeholder, treat as empty string
      // No error required unless you want it mandatory. Keeping as optional.
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!validate()) return;
    let translationsToUse = translations;
    // Only call the translate API when there are no translations yet.
    // If they were already generated (via preview modal or a prior submit attempt),
    // reuse them as-is — don't call the API again.
    if (!translationsToUse || Object.keys(translationsToUse).length === 0) {
      try {
        setIsTranslating(true);
        const result = await SubscriptionsService.translateSubscriptionContent({
          name: formData.name,
          name_sub_title: formData.name_sub_title,
          description: formData.description,
          source_language: currentLanguage.code,
        });
        translationsToUse = result;
        setTranslations(result);
      } catch (error: unknown) {
        const e = error as {
          response?: { data?: { message?: string } };
          message?: string;
        };
        const msg =
          e?.response?.data?.message ||
          e?.message ||
          'Failed to prepare translations';
        enqueueSnackbar(msg, { variant: 'error' });
      } finally {
        setIsTranslating(false);
      }
    }

    const finalTranslations = translationsToUse ? { ...translationsToUse } : {};
    const srcLang = currentLanguage.code;
    finalTranslations[srcLang] = {
      ...finalTranslations[srcLang],
      name: formData.name,
      name_sub_title: formData.name_sub_title,
      description: formData.description,
    };

    onCreate({
      name: formData.name,
      name_sub_title: formData.name_sub_title,
      description: formData.description || '',
      price: formData.price !== undefined ? Number(formData.price) : undefined,
      billing_cycle: formData.billing_cycle || BillingCycle.MONTHLY,
      is_active: Boolean(formData.is_active),
      status: (formData.status as number) ?? SubscriptionStatus.ACTIVE,
      subscription_type: (formData.subscription_type as number) ?? undefined,
      language: currentLanguage.code,
      translations: JSON.stringify(finalTranslations),
      apple_product_id: formData.apple_product_id || null,
    });
  };

  return (
    <>
      <Modal
        open={isOpen}
        onClose={onClose}
        className="!flex items-center justify-center"
      >
        <div
          className="subscription-modal responsive-form bg-gray-100 p-6 w-full max-w-4xl mx-4 rounded-lg shadow-lg flex flex-col max-h-[90vh]"
          role="dialog"
          aria-modal="true"
          aria-labelledby="add-subscription-title"
          aria-describedby="add-subscription-desc"
        >
          <div className="flex justify-between items-center mb-4">
            <h1
              id="add-subscription-title"
              className="text-lg font-medium text-gray-900"
            >
              {intl.formatMessage({ id: 'SUBSCRIPTION.MODAL.TITLE' })}
            </h1>
            <div className="flex items-center gap-3">
              <div className="relative">
                <select
                  id="language-select"
                  name="language"
                  autoComplete="off"
                  className="select select-bordered pl-7 pr-8 h-10 leading-normal"
                  value={currentLanguage.code}
                  onChange={(e) => {
                    const selected = I18N_LANGUAGES.find(
                      (l) => l.code === e.target.value
                    );
                    if (selected) changeLanguage(selected);
                  }}
                  aria-label={intl.formatMessage({ id: 'USER.MENU.LANGUAGE' })}
                >
                  {I18N_LANGUAGES.map((lang) => (
                    <option key={lang.code} value={lang.code}>
                      {lang.label}
                    </option>
                  ))}
                </select>
                <img
                  src="/media/flags/globe.svg"
                  alt="Globe"
                  className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 invert pointer-events-none"
                />
              </div>
            </div>
          </div>
          {/* SR-only description for screen readers */}
          <p id="add-subscription-desc" className="sr-only">
            Fill in subscription details and submit to create a new plan.
          </p>

          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleSubmit();
            }}
          >
            <div className="flex-1 overflow-y-auto max-h-[70vh] custom-scrollbar">
              <div className="flex flex-col gap-6 pr-3">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Name Field */}
                  <div className="space-y-2 md:col-span-2">
                    <label
                      className="block text-sm font-medium text-gray-700"
                      htmlFor="subscription_name_editor"
                    >
                      {intl.formatMessage({ id: 'SUBSCRIPTION.MODAL.NAME' })} *
                    </label>
                    <Editor
                      id="subscription_name_editor"
                      tinymceScriptSrc={TINYMCE_SCRIPT_SRC}
                      value={formData.name || ''}
                      init={{
                        ...tinymceInit,
                        height: 300,
                        menubar: true,
                        toolbar: [
                          'undo redo | blocks | bold italic underline strikethrough | forecolor backcolor',
                          'alignleft aligncenter alignright alignjustify | bullist numlist | removeformat',
                        ],
                      }}
                      onEditorChange={(content: string) => {
                        setFormData((prev) => ({ ...prev, name: content }));
                        if (formErrors.name)
                          setFormErrors((prev) => ({ ...prev, name: '' }));
                      }}
                      textareaName="subscription_name"
                      aria-label={`${intl.formatMessage({ id: 'SUBSCRIPTION.MODAL.NAME' })} *`}
                    />
                    {formErrors.name && (
                      <p className="text-sm text-red-500">{formErrors.name}</p>
                    )}
                  </div>

                  {/* Price */}
                  <div className="space-y-2">
                    <label
                      htmlFor="price"
                      className="block text-sm font-medium text-gray-700"
                    >
                      {intl.formatMessage({ id: 'SUBSCRIPTION.MODAL.PRICE' })}
                    </label>
                    <PriceInput
                      id="price"
                      name="price"
                      value={formData.price ?? ''}
                      autoComplete="transaction-amount"
                      aria-label={intl.formatMessage({
                        id: 'SUBSCRIPTION.MODAL.PRICE',
                      })}
                      onChange={(val) => {
                        setFormData((prev) => ({
                          ...prev,
                          price: val,
                        }));
                        if (formErrors.price) {
                          setFormErrors((prev) => ({ ...prev, price: '' }));
                        }
                      }}
                      inputClassName={`w-full h-11 px-3 py-0 leading-normal border-2 ${formErrors.price ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:border-primary focus:ring-0`}
                      error={formErrors.price}
                      placeholder={intl.formatMessage({
                        id: 'SUBSCRIPTION.MODAL.PRICE_PLACEHOLDER',
                      })}
                    />
                  </div>

                  {/* Billing Cycle */}
                  <div className="space-y-2">
                    <label
                      className="block text-sm font-medium text-gray-700"
                      htmlFor="billing_cycle"
                    >
                      {intl.formatMessage({
                        id: 'SUBSCRIPTION.MODAL.BILLING_CYCLE',
                      })}
                    </label>
                    <select
                      id="billing_cycle"
                      name="billing_cycle"
                      autoComplete="off"
                      value={Number(
                        formData.billing_cycle ?? BillingCycle.MONTHLY
                      )}
                      onChange={handleInputChange}
                      className="input input-bordered w-full h-11 px-3 py-0 leading-normal rounded-lg border-2 border-gray-300 focus:outline-none focus:border-primary focus:ring-0"
                    >
                      <option value={BillingCycle.MONTHLY}>
                        {intl.formatMessage({
                          id: 'SUBSCRIPTION.MODAL.BILLING_CYCLE.MONTHLY',
                        })}
                      </option>
                      <option value={BillingCycle.YEARLY}>
                        {intl.formatMessage({
                          id: 'SUBSCRIPTION.MODAL.BILLING_CYCLE.YEARLY',
                        })}
                      </option>
                      <option value={BillingCycle.FIVE_DAYS}>
                        {intl.formatMessage({
                          id: 'SUBSCRIPTION.MODAL.BILLING_CYCLE.FIVE_DAYS',
                        })}
                      </option>
                    </select>
                  </div>

                  {/* Subscription Type */}
                  <div className="space-y-2">
                    <label
                      className="block text-sm font-medium text-gray-700"
                      htmlFor="subscription_type"
                    >
                      {intl.formatMessage({
                        id: 'SUBSCRIPTION.MODAL.SUBSCRIPTION_TYPE',
                      })}
                      <span className="text-red-500"> *</span>
                    </label>
                    <select
                      id="subscription_type"
                      name="subscription_type"
                      autoComplete="off"
                      value={String(formData.subscription_type ?? '')}
                      onChange={handleInputChange}
                      className={`input input-bordered w-full h-11 px-3 py-0 leading-normal rounded-lg border-2 ${formErrors.subscription_type ? 'border-red-500' : 'border-gray-300'} focus:outline-none focus:border-primary focus:ring-0`}
                    >
                      <option value="">
                        {intl.formatMessage({
                          id: 'SUBSCRIPTION.MODAL.SUBSCRIPTION_TYPE.SELECT',
                        })}
                      </option>
                      <option value={SubscriptionType.BOATER}>
                        {intl.formatMessage({ id: 'SUBSCRIPTION.TYPE.BOATER' })}
                      </option>
                      <option value={SubscriptionType.HARBOR}>
                        {intl.formatMessage({ id: 'SUBSCRIPTION.TYPE.HARBOR' })}
                      </option>
                      <option value={SubscriptionType.CLUB}>
                        {intl.formatMessage({ id: 'SUBSCRIPTION.TYPE.CLUB' })}
                      </option>
                      <option value={SubscriptionType.DMO}>
                        {intl.formatMessage({
                          id: 'SUBSCRIPTION.TYPE.DMO',
                        })}
                      </option>
                      <option value={SubscriptionType.SERVICE_PROVIDER}>
                        {intl.formatMessage({
                          id: 'SUBSCRIPTION.TYPE.SERVICE_PROVIDER',
                        })}
                      </option>
                    </select>
                    {formErrors.subscription_type && (
                      <p className="text-sm text-red-500">
                        {formErrors.subscription_type}
                      </p>
                    )}
                  </div>

                  {/* Apple Product ID */}
                  <div className="space-y-2">
                    <label
                      className="block text-sm font-medium text-gray-700"
                      htmlFor="apple_product_id"
                    >
                      {intl.formatMessage({
                        id: 'SUBSCRIPTION.MODAL.APPLE_PRODUCT_ID',
                      })}
                    </label>
                    <input
                      type="text"
                      id="apple_product_id"
                      name="apple_product_id"
                      autoComplete="off"
                      value={formData.apple_product_id ?? ''}
                      onChange={handleInputChange}
                      className="input input-bordered w-full h-11 px-3 py-0 leading-normal rounded-lg border-2 border-gray-300 focus:outline-none focus:border-primary focus:ring-0"
                      placeholder={intl.formatMessage({
                        id: 'SUBSCRIPTION.MODAL.APPLE_PRODUCT_ID_PLACEHOLDER',
                      })}
                    />
                  </div>

                  {/* Name Sub Title + Status */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:col-span-2">
                    {/* Name Sub Title */}
                    <div className="space-y-2">
                      <label
                        className="block text-sm font-medium text-gray-700"
                        htmlFor="name_sub_title"
                      >
                        {intl.formatMessage({
                          id: 'SUBSCRIPTION.MODAL.SUBTITLE',
                        })}
                      </label>
                      <input
                        type="text"
                        id="name_sub_title"
                        name="name_sub_title"
                        autoComplete="off"
                        value={formData.name_sub_title ?? ''}
                        onChange={handleInputChange}
                        className="input input-bordered w-full h-11 px-3 py-0 leading-normal rounded-lg border-2 border-gray-300 focus:outline-none focus:border-primary focus:ring-0"
                        placeholder={intl.formatMessage({
                          id: 'SUBSCRIPTION.MODAL.SUBTITLE_PLACEHOLDER',
                        })}
                      />
                    </div>
                    {/* Status */}
                    <div className="space-y-2">
                      <label
                        className="block text-sm font-medium text-gray-700"
                        htmlFor="status"
                      >
                        {intl.formatMessage({
                          id: 'SUBSCRIPTION.MODAL.STATUS',
                        })}
                      </label>
                      <select
                        id="status"
                        name="status"
                        autoComplete="off"
                        value={String(
                          formData.status ?? SubscriptionStatus.ACTIVE
                        )}
                        onChange={handleInputChange}
                        className="input input-bordered w-full h-11 px-3 py-0 leading-normal rounded-lg border-2 border-gray-300 focus:outline-none focus:border-primary focus:ring-0"
                      >
                        <option value={SubscriptionStatus.ACTIVE}>
                          {intl.formatMessage({
                            id: 'COMMON.ACTIVE',
                          })}
                        </option>
                        <option value={SubscriptionStatus.INACTIVE}>
                          {intl.formatMessage({
                            id: 'COMMON.INACTIVE',
                          })}
                        </option>
                      </select>
                    </div>
                  </div>

                  {/* Description */}
                  <div className="space-y-2 md:col-span-2">
                    <label
                      className="flex items-center gap-2 text-sm font-medium text-gray-700"
                      htmlFor="subscription_description_editor"
                    >
                      {intl.formatMessage({
                        id: 'SUBSCRIPTION.MODAL.DESCRIPTION',
                      })}
                      <DefaultTooltip
                        title={
                          <div className="whitespace-pre-line">
                            {intl.formatMessage({
                              id: 'SUBSCRIPTION.MODAL.DESCRIPTION.TOOLTIP',
                            })}
                          </div>
                        }
                        placement="top"
                        arrow
                      >
                        <svg
                          className="w-4 h-4 text-gray-400 hover:text-gray-600 cursor-help"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            fillRule="evenodd"
                            d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </DefaultTooltip>
                    </label>
                    <Editor
                      id="subscription_description_editor"
                      tinymceScriptSrc={TINYMCE_SCRIPT_SRC}
                      value={formData.description || ''}
                      init={{
                        ...tinymceInit,
                        height: 350,
                        menubar: true,
                      }}
                      onEditorChange={(content: string) => {
                        setFormData((prev) => ({
                          ...prev,
                          description: content,
                        }));
                      }}
                      textareaName="subscription_description"
                      aria-label={intl.formatMessage({
                        id: 'SUBSCRIPTION.MODAL.DESCRIPTION',
                      })}
                    />
                  </div>
                </div>

                <div className="flex flex-wrap justify-end gap-3 mt-6 pt-4 border-t border-gray-200 m-2 p-2">
                  <div className="w-full sm:w-auto mr-auto">
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={handleOpenPreview}
                      disabled={
                        isCreating ||
                        isTranslating ||
                        isRichTextEmpty(formData.name)
                      }
                    >
                      {isTranslating ? (
                        <>
                          <svg
                            className="animate-spin mr-2 h-4 w-4"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                          >
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                            ></circle>
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            ></path>
                          </svg>
                          {intl.formatMessage({
                            id: 'SUBSCRIPTION.MODAL.BUTTON.PREPARING_PREVIEW',
                          })}
                        </>
                      ) : (
                        intl.formatMessage({
                          id: 'SUBSCRIPTION.MODAL.BUTTON.PREVIEW',
                        })
                      )}
                    </Button>
                  </div>
                  <div className="w-full sm:w-auto">
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={onClose}
                      disabled={isCreating}
                    >
                      {intl.formatMessage({
                        id: 'SUBSCRIPTION.MODAL.BUTTON.CANCEL',
                      })}
                    </Button>
                  </div>
                  <div className="w-full sm:w-auto">
                    <Button
                      type="submit"
                      variant="primary"
                      disabled={isCreating}
                    >
                      {isCreating ? (
                        <>
                          <svg
                            className="animate-spin mr-2 h-4 w-4 text-white"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                          >
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                            ></circle>
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            ></path>
                          </svg>
                          {intl.formatMessage({
                            id: 'SUBSCRIPTION.MODAL.BUTTON.CREATING',
                          })}
                        </>
                      ) : (
                        intl.formatMessage({
                          id: 'SUBSCRIPTION.MODAL.BUTTON.CREATE',
                        })
                      )}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </form>
        </div>
      </Modal>

      {/* Translations Preview Modal */}
      <SubscriptionPreviewModal
        open={openPreview}
        onClose={() => setOpenPreview(false)}
        formData={{
          name: formData.name,
          name_sub_title: formData.name_sub_title,
          description: formData.description,
        }}
        source_language={currentLanguage.code}
        initialTranslations={translations}
        onTranslationsGenerated={(t) => setTranslations(t)}
        onSourceFormDataChange={(fields) =>
          setFormData((prev) => ({ ...prev, ...fields }))
        }
      />
    </>
  );
};

export default memo(AddSubscriptionModal);
